import { CanActivateFn } from '@angular/router';

/**
 * SessionStorage key for post-login redirect URL (PC-1636).
 * When user completes login we store the dashboard URL here before redirecting.
 * If user presses Back and lands on the login app, this guard redirects them back to dashboard.
 */
export const AUTH_REDIRECT_URL_KEY = 'auth_redirect_url';

/**
 * LocalStorage key for the last authenticated redirect URL (V2CORE-715).
 * Stored after successful login so new browser tabs can redirect to the target app
 * instead of showing the login form again.
 */
export const LAST_AUTHENTICATED_REDIRECT_URL_KEY = 'last_authenticated_redirect_url';

/**
 * SessionStorage key to prevent redirect loops (V2CORE-715).
 * Set before redirecting to the target app. If the target app's session is invalid
 * and it redirects back to login, this flag prevents an infinite redirect loop.
 * SessionStorage is per-tab and cleared when the tab closes, allowing fresh checks in new tabs.
 */
export const SESSION_CHECK_ATTEMPTED_KEY = 'session_check_attempted';

/**
 * Guard: If user already completed login (auth_redirect_url in sessionStorage), redirect back to
 * dashboard so Back button does not leave them on the login screen (PC-1636).
 *
 * Also handles V2CORE-715: If a stored redirect URL exists in localStorage and no session check
 * has been attempted in this tab, redirect to the target app to reuse an existing session.
 */
export const authRedirectGuard: CanActivateFn = () => {
  if (typeof sessionStorage === 'undefined') return true;

  const params = new URLSearchParams(window.location.search);

  // SSO callback: let the SsoComponent handle the authorization code exchange
  if (params.has('code')) {
    return true;
  }

  // If the main app sent user here after logout, clear stale redirects and show login form
  if (params.get('logout') === 'true') {
    sessionStorage.removeItem(AUTH_REDIRECT_URL_KEY);
    localStorage.removeItem(LAST_AUTHENTICATED_REDIRECT_URL_KEY);
    sessionStorage.removeItem(SESSION_CHECK_ATTEMPTED_KEY);

    // Nuke browser history so Back button cannot reach cached pre-logout pages.
    // 1. Replace current entry with clean URL (removes ?logout=true)
    const cleanUrl = window.location.pathname;
    window.history.replaceState(null, '', cleanUrl);
    // 2. Push a fresh entry so there is nothing behind it to go back to
    window.history.pushState(null, '', cleanUrl);
    // 3. Trap any back-navigation: if user presses Back, immediately push forward again
    window.addEventListener('popstate', () => {
      window.history.pushState(null, '', cleanUrl);
    });

    return true;
  }

  // Skip all redirects when the URL carries an invitation_id – the sign-in
  // component must handle the invitation flow regardless of prior auth state.
  const currentParams = new URLSearchParams(window.location.search);
  if (currentParams.has('invitation_id')) {
    return true;
  }

  // PC-1636: Back button redirect
  const redirectUrl = sessionStorage.getItem(AUTH_REDIRECT_URL_KEY);
  if (redirectUrl) {
    sessionStorage.removeItem(AUTH_REDIRECT_URL_KEY);
    window.location.replace(redirectUrl);
    return false;
  }

  // V2CORE-715: New tab redirect for already-authenticated users
  const lastAuthUrl = localStorage.getItem(LAST_AUTHENTICATED_REDIRECT_URL_KEY);
  if (lastAuthUrl && !sessionStorage.getItem(SESSION_CHECK_ATTEMPTED_KEY)) {
    sessionStorage.setItem(SESSION_CHECK_ATTEMPTED_KEY, 'true');
    window.location.replace(lastAuthUrl);
    return false;
  }

  return true;
};
